import {Box, CardContent, CardHeader, FormHelperText, Typography} from "@mui/material";
import {EditForm, IntegerNumberFormat, NumberField, RadioGroup} from "@variocube/app-ui";
import React, {ReactNode, useEffect, useState} from "react";
import {BookingSettings} from "../../api";
import {useLocalization, useWordingFlavor} from "@smalox/shared";

interface BookingSettingsEditFormProps {
	loading: boolean;
	bookingSettings?: BookingSettings;
	onSave: (bookingSettings: BookingSettings) => Promise<any>;
	title?: ReactNode;
	hideNotSpecified?: boolean;
}

export function BookingSettingsEditForm(props: Readonly<BookingSettingsEditFormProps>) {
	const {t} = useLocalization();
	const wordingFlavor = useWordingFlavor();
	const {loading, bookingSettings, onSave, title, hideNotSpecified} = props;

	const [maxBookingsPerUser, setMaxBookingsPerUser] = useState<number | null>(null);
	const [temporary, setTemporary] = useState<boolean | null>(null);
	const [remotelyBookable, setRemotelyBookable] = useState<boolean | null>(null);

	useEffect(() => {
		if (bookingSettings) {
			setMaxBookingsPerUser(bookingSettings.maxBookingsPerUser ?? null);
			setTemporary(bookingSettings.temporary ?? null);
			setRemotelyBookable(bookingSettings.remotelyBookable ?? null);
		}
	}, [bookingSettings]);

	async function handleSave() {
		await onSave({
			enableUserBookingCreation: false,
			maxBookingsPerUser: maxBookingsPerUser ?? undefined,
			temporary: temporary ?? undefined,
			remotelyBookable: remotelyBookable ?? undefined,
		});
	}

	let options: ("true" | "false" | "null")[] = ["true", "false"];
	if (!hideNotSpecified) {
		options.push("null");
	}

	return (
		<EditForm loading={loading} labels={t} onSave={handleSave}>
			{title && <CardHeader title={title} />}
			<CardContent>
				<NumberField
					label={t(`settings.booking.maxBookingsPerUser.title.${wordingFlavor}`)}
					numberFormat={IntegerNumberFormat}
					numberValue={maxBookingsPerUser}
					onChangeNumber={setMaxBookingsPerUser}
					fullWidth
				/>
				<FormHelperText>{t(`settings.booking.maxBookingsPerUser.helperText.${wordingFlavor}`)}</FormHelperText>
			</CardContent>
			<CardContent>
				<RadioGroup
					label={t(`settings.booking.temporary.title.${wordingFlavor}`)}
					options={options}
					value={triStateBooleanToString(temporary)}
					onChange={value => setTemporary(triStateBooleanFromString(value))}
					renderLabel={option => (
						<Box my={1}>
							<Typography variant="body1">{t(`settings.booking.temporary.options.${option}`)}</Typography>
							<FormHelperText sx={{m: 0}}>
								{option === "null"
									? t("settings.booking.temporary.helperTexts.null")
									: t(`settings.booking.temporary.helperTexts.${option}.${wordingFlavor}` as any)}
							</FormHelperText>
						</Box>
					)}
				/>
			</CardContent>
			<CardContent>
				<RadioGroup
					label={t(`settings.booking.remotelyBookable.title.${wordingFlavor}`)}
					options={options}
					value={triStateBooleanToString(remotelyBookable)}
					onChange={value => setRemotelyBookable(triStateBooleanFromString(value))}
					renderLabel={option => (
						<Box my={1}>
							<Typography variant="body1">{t(`settings.booking.remotelyBookable.options.${option}`)}</Typography>
							<FormHelperText sx={{m: 0}}>
								{option === "null"
									? t("settings.booking.remotelyBookable.helperTexts.null")
									: t(`settings.booking.remotelyBookable.helperTexts.${option}.${wordingFlavor}` as any)}
							</FormHelperText>
						</Box>
					)}
				/>
			</CardContent>
		</EditForm>
	);
}

function triStateBooleanToString(b: boolean | null) {
	if (b == null) {
		return "null";
	}
	if (b) {
		return "true";
	}
	return "false";
}

function triStateBooleanFromString(str: "true" | "false" | "null") {
	switch (str) {
		case "true":
			return true;
		case "false":
			return false;
		case "null":
			return null;
	}
}
